export default function StaffTraining() {
  return (
    <section id="training" className="bg-[#0D0D0D] py-16 sm:py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.2em] text-[#707070] font-light mb-3 sm:mb-4">
            Precision Training
          </div>
          <h2 className="text-[28px] sm:text-[36px] md:text-[48px] font-bold text-[#F5F5F5] mb-4 sm:mb-6 font-serif">
            Elite Medical Security Training
          </h2>
          <p className="text-sm sm:text-base md:text-[16px] font-light text-[#9A9A9A] max-w-[720px] mx-auto leading-relaxed px-4 sm:px-0">
            Bespoke emergency readiness training for aviation crews, yacht crews, estate staff, and security teams—focused on high-impact scenarios and real-world execution.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
          <div className="relative h-[300px] sm:h-[400px] md:h-[480px] rounded-2xl overflow-hidden group">
            <img 
              src="https://public.readdy.ai/ai/img_res/de546c8586da4e5924a6ccf5be2c2b43.jpg"
              alt="Aviation Crew Medical Training"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <i className="ri-plane-line text-xl sm:text-2xl md:text-[28px] text-[#D4C4B0]"></i>
                <h3 className="text-lg sm:text-xl md:text-[24px] font-bold text-[#F5F5F5]">Aviation Crew Training</h3>
              </div>
              <p className="text-xs sm:text-sm md:text-[14px] text-[#C0C0C0] leading-relaxed">
                Advanced emergency protocols for private aviation crews—cardiac events, trauma response, and in-flight medical crisis management.
              </p>
            </div>
          </div>

          <div className="relative h-[300px] sm:h-[400px] md:h-[480px] rounded-2xl overflow-hidden group">
            <img 
              src="https://readdy.ai/api/search-image?query=professional-female-medical-instructor-wearing-crisp-white-U-First-Doctors-branded-polo-shirt-with-gold-caduceus-emblem-teaching-CPR-chest-compression-techniques-to-three-male-yacht-crew-members-in-pristine-white-uniforms-on-luxury-superyacht-deck-hands-on-medical-training-demonstration-with-CPR-mannequin-positioned-on-helicopter-landing-pad-area-crystal-clear-turquoise-ocean-horizon-background-bright-daylight-professional-maritime-emergency-response-training-sophisticated-yacht-environment-high-quality-realistic-training-scene-focused-learning-atmosphere-spacious-yacht-deck-setting&width=800&height=960&seq=femaletraineryachtcpr008&orientation=portrait"
              alt="Maritime Crew Medical Training"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <i className="ri-ship-line text-xl sm:text-2xl md:text-[28px] text-[#D4C4B0]"></i>
                <h3 className="text-lg sm:text-xl md:text-[24px] font-bold text-[#F5F5F5]">Maritime Crew Training</h3>
              </div>
              <p className="text-xs sm:text-sm md:text-[14px] text-[#C0C0C0] leading-relaxed">
                Superyacht crew medical readiness—offshore emergency response, evacuation protocols, and maritime medical security.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {[
            {
              image: 'https://readdy.ai/api/search-image?query=smiling-Black-African-American-lady-estate-staff-member-and-white-male-colleague-holding-AED-automated-external-defibrillator-machine-learning-emergency-response-with-muscular-built-medical-instructor-wearing-white-t-shirt-demonstrating-proper-AED-technique-in-elegant-luxury-private-residence-sophisticated-interior-setting-high-end-estate-environment-focused-professional-training-session-friendly-atmosphere-hands-on-medical-equipment-demonstration-well-lit-training-area-professional-emergency-response-training-diverse-estate-staff&width=600&height=720&seq=estateaedtrainingdiversity004&orientation=portrait',
              icon: 'ri-home-4-line',
              title: 'Estate Staff',
              description: 'Discreet emergency readiness for household and security teams.'
            },
            {
              image: 'https://readdy.ai/api/search-image?query=two-medical-trainers-wearing-white-t-shirts-teaching-CPR-emergency-response-to-Black-African-American-male-security-professional-wearing-dark-business-suit-professional-training-facility-modern-environment-hands-on-medical-demonstration-focused-learning-atmosphere-well-lit-training-room-medical-training-mannequin-visible-diverse-training-team-emergency-response-skills-development-professional-instructors-in-white-shirts&width=600&height=720&seq=securitytwoshirtstraining005&orientation=portrait',
              icon: 'ri-shield-user-line',
              title: 'Security Teams',
              description: 'Tactical medical response integrated with executive protection.'
            },
            {
              image: 'https://readdy.ai/api/search-image?query=luxury-hotel-staff-receiving-elite-medical-emergency-training-professional-hotel-crew-learning-VIP-guest-medical-response-protocols-five-star-hotel-setting-sophisticated-training-environment&width=600&height=720&seq=hoteltraining001&orientation=portrait',
              icon: 'ri-hotel-line',
              title: 'Elite Hotel Crews',
              description: 'VIP guest medical security protocols for luxury hospitality.'
            }
          ].map((item, index) => (
            <div key={index} className="relative h-[250px] sm:h-[300px] md:h-[360px] rounded-2xl overflow-hidden group">
              <img 
                src={item.image}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 md:p-6">
                <div className="flex items-center gap-2 mb-1 sm:mb-2">
                  <i className={`${item.icon} text-lg sm:text-xl md:text-[24px] text-[#D4C4B0]`}></i>
                  <h3 className="text-base sm:text-lg md:text-[20px] font-bold text-[#F5F5F5]">{item.title}</h3>
                </div>
                <p className="text-xs sm:text-sm md:text-[13px] text-[#C0C0C0] leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a 
            href="/services" 
            className="inline-block px-8 py-4 bg-[#D4C4B0] text-black text-[14px] font-semibold rounded-lg hover:bg-[#C4B4A0] transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            View All Training Programs
          </a>
        </div>

        <div className="mt-20 bg-[#151515] border border-[#252525] rounded-2xl p-12">
          <div className="grid grid-cols-2 gap-16 items-center">
            <div>
              <h3 className="text-[32px] font-bold text-[#F5F5F5] mb-6 font-serif">
                Training That Saves Lives
              </h3>
              <p className="text-[15px] text-[#9A9A9A] leading-relaxed mb-8">
                Our training programs are designed for high-stakes environments where seconds matter. Every scenario is based on real-world incidents, every protocol tested under pressure.
              </p>
              <div className="space-y-4">
                {[
                  'Cardiac arrest response',
                  'Trauma management',
                  'Evacuation coordination',
                  'Crisis communication',
                  'Equipment deployment'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-[#D4C4B0] rounded-full"></div>
                    <span className="text-[14px] text-[#C0C0C0]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-[400px] rounded-xl overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=professional-medical-instructor-demonstrating-advanced-emergency-response-techniques-to-elite-crew-members-in-modern-training-facility-serious-focused-training-session-medical-equipment-and-mannequins-visible-high-end-professional-environment-cinematic-lighting&width=700&height=800&seq=training007&orientation=portrait"
                alt="Professional Medical Training"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            </div>
          </div>
        </div>

        <div className="mt-32">
          <div className="text-center mb-16">
            <h3 className="text-[40px] font-bold text-[#F5F5F5] mb-4 font-serif">
              Comprehensive Skillsets For Life
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-12 mb-16">
            <div className="bg-[#111111] border border-[#222222] rounded-2xl p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#1A1815] rounded-xl flex items-center justify-center">
                  <i className="ri-heart-pulse-line text-[24px] text-[#D4C4B0]"></i>
                </div>
                <div>
                  <h4 className="text-[20px] font-bold text-[#F5F5F5]">CPR, Choking & Seizure Management, AED Certification</h4>
                  <p className="text-[13px] text-[#808080]">Internationally recognized life-saving certification</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  'Adult & Pediatric CPR',
                  'AED Operation',
                  'Choking Management',
                  'Seizure Management',
                  'Annual Recertification'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-[#D4C4B0] rounded-full"></div>
                    <span className="text-[14px] text-[#C0C0C0]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#111111] border border-[#222222] rounded-2xl p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#1A1815] rounded-xl flex items-center justify-center">
                  <i className="ri-first-aid-kit-line text-[24px] text-[#D4C4B0]"></i>
                </div>
                <div>
                  <h4 className="text-[20px] font-bold text-[#F5F5F5]">Advanced First Aid</h4>
                  <p className="text-[13px] text-[#808080]">Advanced techniques for serious medical emergencies</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  'Trauma Management',
                  'Wound Care',
                  'Fracture Stabilization',
                  'Medical Assessment'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-[#D4C4B0] rounded-full"></div>
                    <span className="text-[14px] text-[#C0C0C0]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#111111] border border-[#222222] rounded-2xl p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#1A1815] rounded-xl flex items-center justify-center">
                  <i className="ri-ship-2-line text-[24px] text-[#D4C4B0]"></i>
                </div>
                <div>
                  <h4 className="text-[20px] font-bold text-[#F5F5F5]">Sea & Air Sickness Management</h4>
                  <p className="text-[13px] text-[#808080]">Specialized treatment for aviation and maritime environments</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  'Motion Sickness Prevention',
                  'Symptom Management',
                  'Medication Protocols',
                  'Environmental Adaptation'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-[#D4C4B0] rounded-full"></div>
                    <span className="text-[14px] text-[#C0C0C0]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#111111] border border-[#222222] rounded-2xl p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#1A1815] rounded-xl flex items-center justify-center">
                  <i className="ri-movie-2-line text-[24px] text-[#D4C4B0]"></i>
                </div>
                <div>
                  <h4 className="text-[20px] font-bold text-[#F5F5F5]">Scenario-Based Training</h4>
                  <p className="text-[13px] text-[#808080]">Realistic emergency simulations for your environment</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  'Custom Scenarios',
                  'Live Simulations',
                  'Team Coordination',
                  'Stress Management'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-[#D4C4B0] rounded-full"></div>
                    <span className="text-[14px] text-[#C0C0C0]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-12">
            <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-2xl p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#1A1815] rounded-xl flex items-center justify-center">
                  <i className="ri-file-list-3-line text-[24px] text-[#D4C4B0]"></i>
                </div>
                <div>
                  <h4 className="text-[20px] font-bold text-[#F5F5F5]">Emergency Protocol Development</h4>
                  <p className="text-[13px] text-[#808080]">Customized response protocols for your operations</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  'Risk Assessment',
                  'Protocol Design',
                  'Documentation',
                  'Regular Updates'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-[#D4C4B0] rounded-full"></div>
                    <span className="text-[14px] text-[#C0C0C0]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-2xl p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#1A1815] rounded-xl flex items-center justify-center">
                  <i className="ri-24-hours-line text-[24px] text-[#D4C4B0]"></i>
                </div>
                <div>
                  <h4 className="text-[20px] font-bold text-[#F5F5F5]">24/7 Medical Consultation</h4>
                  <p className="text-[13px] text-[#808080]">Round-the-clock access to medical professionals</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  'Immediate Response',
                  'Expert Guidance',
                  'Remote Support',
                  'Follow-up Care'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-[#D4C4B0] rounded-full"></div>
                    <span className="text-[14px] text-[#C0C0C0]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
